import type { GroupingState, RowSelectionState } from "@tanstack/react-table";

import { RequestTypeEnum } from "@nlc/shared-types";
import { Link, useSearch } from "@tanstack/react-router";
import { ExternalLinkIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types";

import { Button } from "@/components/button";
import { MyFilesKickstarterBanner } from "@/components/kickstarter-banner/banners";
import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";
import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard.tsx";
import { MyFilesModalsProvider } from "@/features/my-files/contexts/my-files-modals.context.tsx";
import { useIsMyFileTemplate } from "@/features/my-files/hooks/useIsMyFileTemplate.tsx";
import { useIsMobileScreen } from "@/hooks/useIsMobileScreen";

import type { FileItem, View } from "./types";

import { Breadcrumbs } from "./components/breadcrumbs";
import { FileDeleteDialog } from "./components/file-delete-dialog";
import { FileRestoredDialog } from "./components/file-restored-dialog";
import { FilesGridView } from "./components/files-grid-view";
import { FilesListView } from "./components/files-list-view";
import { GDriveSyncButton } from "./components/GDriveSyncButton";
import { SelectionActions } from "./components/selection-actions";
import { ToggleViewButtons } from "./components/toggle-view-buttons";
import { useGetMyFilesStructure } from "./hooks/useGetMyFilesStructure";
import { useGoogleDriveStatus } from "./hooks/useGoogleDriveOAuth";
import { useMyFilesTable } from "./hooks/useMyFilesTable";
import { useNavigationListener } from "./hooks/useNavigationListener";

const EMPTY_STRUCTURE = { breadcrumbs: [], files: [] as FileItem[] };

export function MyFiles() {
  return (
    <MyFilesModalsProvider>
      <MediaPreviewModalProvider>
        <MyFilesContent />
      </MediaPreviewModalProvider>
    </MyFilesModalsProvider>
  );
}

function MyFilesContent() {
  const [view, setView] = useState<View>("list");
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const isMobile = useIsMobileScreen();
  const { setMediaItems } = useMediaPreviewModal();

  const { brandId, folder, projectId, requestId, requestType } = useSearch({
    strict: false,
  });

  const { data: gdriveStatus } = useGoogleDriveStatus();

  const isTemplate = useIsMyFileTemplate();

  const {
    data = EMPTY_STRUCTURE,
    error,
    isFetching,
    refetch,
  } = useGetMyFilesStructure({
    brandId,
    folder,
    projectId,
    requestId,
    requestType,
  });

  const { breadcrumbs, files: transformedFileItems = [] } = data;
  const isForbidden =
    error && "statusCode" in error && error.statusCode === 400;

  useEffect(() => {
    refetch();
  }, [brandId, projectId, requestId, requestType, folder, refetch]);

  const table = useMyFilesTable({
    data: transformedFileItems,
    grouping,
    isMobile,
    rowSelection,
    setGrouping,
    setRowSelection,
    view,
  });

  useNavigationListener(() => {
    table.resetSorting();
    table.resetRowSelection();
  });

  const mediaItems: MediaItem[] = useMemo(
    () =>
      transformedFileItems
        .filter((file) => file.type === "file")
        .map((file) => ({
          extension: (file as Extract<FileItem, { type: "file" }>).extension,
          id: String(file.id),
          name: file.name,
          url: (file as Extract<FileItem, { type: "file" }>).url,
        })),
    [transformedFileItems],
  );

  useEffect(() => {
    setMediaItems(mediaItems);
  }, [mediaItems]);

  return (
    <>
      <div className="w-full sm:p-4 bg-bg-tertiary">
        <MyFilesKickstarterBanner />
        <div className="mx-auto w-full h-full rounded-xl bg-bg-primary">
          <div className="flex flex-col divide-y divide-border-secondary">
            <div className="flex items-center justify-between px-3xl py-2xl h-[80px]">
              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                onLinkClick={() => {
                  if (!isTemplate) {
                    table.resetSorting();
                    table.resetRowSelection();
                  }
                }}
              />
              <FeatureGuard feature="my-files.gdrive-sync.view">
                {gdriveStatus?.featureFlagEnabled && (
                  <div className="ml-auto">
                    <GDriveSyncButton />
                  </div>
                )}
              </FeatureGuard>
            </div>

            {!isForbidden && (
              <div className="flex justify-between px-3xl py-lg">
                <SelectionActions
                  isTemplate={isTemplate}
                  rowSelection={rowSelection}
                  table={table}
                  view={view}
                />
                <div className="flex items-center gap-lg">
                  {!isTemplate &&
                    !requestId &&
                    brandId &&
                    brandId !== "unknown" && (
                      <Button asChild color="gray" variant="secondary">
                        <Link
                          params={{ brandId: String(brandId) }}
                          to="/brands/$brandId/overview"
                        >
                          <ExternalLinkIcon />
                          {isMobile ? "" : "View Brand Details"}
                        </Link>
                      </Button>
                    )}
                  {(requestId || isTemplate) && (
                    <Button asChild color="gray" variant="secondary">
                      {isTemplate ? (
                        <Link
                          search={{
                            "request-type":
                              requestType === "video"
                                ? RequestTypeEnum.VIDEO
                                : RequestTypeEnum.GRAPHIC,
                          }}
                          to="/requests/template"
                        >
                          <ExternalLinkIcon />
                          {isMobile ? "" : "View Request Details"}
                        </Link>
                      ) : (
                        <Link
                          params={{ requestId: Number(requestId) }}
                          to="/requests/$requestId/edit"
                        >
                          <ExternalLinkIcon />
                          {isMobile ? "" : "View Request Details"}
                        </Link>
                      )}
                    </Button>
                  )}
                  <ToggleViewButtons
                    setGrouping={setGrouping}
                    setView={setView}
                    view={view}
                  />
                </div>
              </div>
            )}

            <div className="px-3xl py-2xl">
              {isForbidden ? (
                <div className="flex h-[calc(100dvh-310px)] w-full items-center justify-center text-text-secondary">
                  <span className="text-lg font-medium">
                    You don&apos;t have access to this folder
                  </span>
                </div>
              ) : (
                <>
                  {view === "grid" && (
                    <FilesGridView isLoading={isFetching} table={table} />
                  )}
                  {view === "list" && (
                    <FilesListView isLoading={isFetching} table={table} />
                  )}
                </>
              )}
            </div>
            <FileRestoredDialog />
            <FileDeleteDialog resetRowSelection={table.resetRowSelection} />
          </div>
        </div>
      </div>
    </>
  );
}
